<!--
// app/components/Shared/SliderWrapper.vue
-->
<script setup lang="ts">
import LeftArrow from "~/components/Shared/icons/LeftArrow.vue";
import RightArrow from "~/components/Shared/icons/RightArrow.vue";
import { useTouchDevice } from "~/composables/device/useTouchDevice";

/**
 * SliderWrapper Component
 *
 * A reusable wrapper component that adds arrow navigation to horizontal scroll containers.
 * Designed for non-touch devices with intelligent arrow visibility based on scroll position.
 *
 * @example
 * <SliderWrapper
 *   :outer-arrows="false"
 *   :scroll-distance="300"
 *   :enable-bounce-animation="true"
 *   auto-scroll-selector=".card-match.live-match, .card-match[data-last-played]"
 * >
 *   <div class="flex gap-4 overflow-x-auto">
 *     <!-- Your scrollable content here -->
 *   </div>
 * </SliderWrapper>
 */

type Props = {
  /**
   * Whether to position arrows outside the container (true) or inside overlaying content (false)
   * @default false
   */
  outerArrows?: boolean;

  /**
   * Distance in pixels to scroll when clicking arrows.
   * If not specified, defaults to 50% of the visible container width
   */
  scrollDistance?: number;

  /**
   * Enable bounce/swing animation on mount to hint at scrollable content
   * Only plays if slider is at the start position
   * @default false
   */
  enableBounceAnimation?: boolean;

  /**
   * CSS selector(s) for auto-scrolling to a specific element on mount
   * Supports multiple selectors separated by commas (tries in order)
   * @example ".live-match, .featured-item"
   */
  autoScrollSelector?: string;

  /**
   * Whether to hide arrows on touch devices
   * @default true
   */
  disableOnTouch?: boolean;

  /**
   * CSS selector for the scrollable element inside the slot
   * If provided, skips automatic detection for better performance
   * @example ".matches-slider-wrapper"
   */
  scrollableSelector?: string;

  /**
   * Whether to hide arrows on mobile devices (screen width < 768px / md breakpoint)
   * When true, arrows will only show on tablet and desktop
   * @default false
   */
  hideArrowsOnMobile?: boolean;

  /**
   * Offset in pixels subtracted from the auto-scroll position on mobile (< 1200px)
   * @default 24
   */
  autoScrollOffsetMobile?: number;

  /**
   * Offset in pixels subtracted from the auto-scroll position on desktop (>= 1200px)
   * @default 324
   */
  autoScrollOffsetDesktop?: number;
};

const props = withDefaults(defineProps<Props>(), {
  outerArrows: false,
  scrollDistance: undefined,
  enableBounceAnimation: false,
  autoScrollSelector: undefined,
  disableOnTouch: true,
  hideArrowsOnMobile: false,
  autoScrollOffsetMobile: 24,
  autoScrollOffsetDesktop: 324,
});

// Touch device detection
const { isTouchDevice } = useTouchDevice();

// Template refs
const sliderContainer = ref<HTMLElement | null>(null);
const sliderContent = ref<HTMLElement | null>(null);
const actualScrollableElement = ref<HTMLElement | null>(null);

// Arrow visibility state
const showLeftArrow = ref<boolean>(false);
const showRightArrow = ref<boolean>(false);

// Bounce animation cancellation handle
let cancelBounce: (() => void) | null = null;

// Auto-scrolling state - used to hide content during initial auto-scroll
// Initialize to true if autoScrollSelector is provided
const isAutoScrolling = ref<boolean>(!!props.autoScrollSelector);

// RTL detection
const isRTL = ref<boolean>(false);

// Mobile detection state (screen width < 768px, md breakpoint)
const isMobile = ref<boolean>(false);

/**
 * Check if arrows should be displayed
 */
const shouldShowArrows = computed(() => {
  if (props.disableOnTouch && isTouchDevice.value) {
    return false;
  }
  if (props.hideArrowsOnMobile && isMobile.value) {
    return false;
  }
  return true;
});

/**
 * Update arrow visibility based on scroll position
 * Accounts for scroll-padding (common with CSS scroll-snap)
 */
const updateArrowVisibility = () => {
  const container = actualScrollableElement.value;

  if (!container) return;

  const scrollLeft = Math.round(container.scrollLeft);
  const scrollWidth = Math.round(container.scrollWidth);
  const clientWidth = Math.round(container.clientWidth);

  // Check if content is scrollable
  const isScrollable = scrollWidth > clientWidth;

  if (!isScrollable) {
    showLeftArrow.value = false;
    showRightArrow.value = false;
    return;
  }

  // Tolerance threshold for considering scroll position as "at start/end"
  // This accounts for scroll-padding used with CSS scroll-snap
  // 15px is a reasonable threshold that covers common scroll-padding values
  const threshold = 15;

  // For LTR
  if (!isRTL.value) {
    // Show left arrow if scrolled past threshold from start
    showLeftArrow.value = scrollLeft > threshold;

    // Show right arrow if not at end (with threshold)
    showRightArrow.value = scrollLeft < scrollWidth - clientWidth - threshold;
  } else {
    // For RTL (scroll positions are negative or reversed depending on browser)
    const isAtStart = Math.abs(scrollLeft) < threshold;
    const isAtEnd = Math.abs(scrollLeft) >= scrollWidth - clientWidth - threshold;

    showLeftArrow.value = !isAtEnd;
    showRightArrow.value = !isAtStart;
  }
};

/**
 * Perform smooth scroll animation
 * Uses native browser scrollBy() for smooth scrolling
 * Works seamlessly with CSS scroll-snap when present
 */
const performScroll = (direction: "left" | "right") => {
  if (!actualScrollableElement.value) return;

  const container = actualScrollableElement.value;

  // Calculate scroll distance
  const distance = props.scrollDistance ?? Math.round(container.clientWidth / 2);

  // Determine scroll direction (negative for left, positive for right)
  const scrollAmount = direction === "left" ? -distance : distance;

  // Use native smooth scrolling - browser handles animation
  // If scroll-snap is enabled, browser will automatically align to snap points
  container.scrollBy({
    left: scrollAmount,
    behavior: "smooth",
  });

  // Update arrow visibility after scroll completes
  // 300ms timeout accounts for smooth scroll animation duration
  setTimeout(() => {
    updateArrowVisibility();
  }, 300);
};

/**
 * Handle left arrow click
 */
const handleLeftClick = () => {
  performScroll("left");
};

/**
 * Handle right arrow click
 */
const handleRightClick = () => {
  performScroll("right");
};

/**
 * Auto-scroll to target element on mount
 * Returns true if auto-scroll was performed, false otherwise
 */
const autoScrollToElement = (): boolean => {
  if (!props.autoScrollSelector || !actualScrollableElement.value) {
    return false;
  }

  const container = actualScrollableElement.value;

  // Split selectors by comma and try each one
  const selectors = props.autoScrollSelector.split(",").map(s => s.trim());

  let targetElement: HTMLElement | null = null;

  for (const selector of selectors) {
    targetElement = container.querySelector(selector);
    if (targetElement) break;
  }

  if (!targetElement) {
    isAutoScrolling.value = false;
    return false;
  }

  // Calculate scroll position directly without resetting to 0 first
  // This avoids the visual "shift" by scrolling instantly to the target position
  const targetX = targetElement.getBoundingClientRect().x;
  const containerX = container.getBoundingClientRect().x;
  const offset = targetX - containerX;

  let scrollPosition: number;
  if (window.innerWidth < 1200) {
    scrollPosition = offset - props.autoScrollOffsetMobile;
  } else {
    scrollPosition = offset - props.autoScrollOffsetDesktop;
  }

  // Set scroll position instantly (no animation, no intermediate state)
  container.scrollLeft = scrollPosition;

  // Update arrow visibility and show content after scroll
  setTimeout(() => {
    updateArrowVisibility();
    isAutoScrolling.value = false;
  }, 100);

  return true; // Auto-scroll was performed
};

/**
 * Initialize bounce animation if enabled.
 * Scrolls the container right then back to give a "peek" hint.
 * Cancelled immediately if the user scrolls manually.
 */
const initializeBounceAnimation = () => {
  if (!props.enableBounceAnimation || !actualScrollableElement.value) return;

  const container = actualScrollableElement.value;
  if (container.scrollLeft !== 0) return;

  let cancelled = false;

  const cancel = () => {
    cancelled = true;
    container.removeEventListener("scroll", cancel);
    cancelBounce = null;
  };

  container.scrollTo({ left: 80, behavior: "smooth" });

  // Wait for the outward scroll to finish before listening for user interaction,
  // otherwise the scroll event from our own scrollTo fires cancel() immediately.
  setTimeout(() => {
    if (cancelled) return;
    container.addEventListener("scroll", cancel, { once: true });
    cancelBounce = cancel;

    setTimeout(() => {
      if (!cancelled) {
        container.removeEventListener("scroll", cancel);
        cancelBounce = null;
        container.scrollTo({ left: 0, behavior: "smooth" });
      }
    }, 400);
  }, 600);
};

/**
 * Find the actual scrollable element
 * If scrollableSelector prop is provided, uses it for direct selection (faster)
 * Otherwise, performs automatic detection
 */
const getScrollableElement = (): HTMLElement | null => {
  if (!sliderContent.value) return null;

  const slotElement = sliderContent.value;

  // Fast path: use provided selector if available
  if (props.scrollableSelector) {
    const scrollableChild = slotElement.querySelector<HTMLElement>(props.scrollableSelector);
    if (scrollableChild) {
      return scrollableChild;
    }
  }

  // Fallback: automatic detection (slower)
  // First, check if the slot content itself is scrollable
  const hasOverflow = slotElement.scrollWidth > slotElement.clientWidth;

  if (hasOverflow) {
    return slotElement;
  }

  // If not, look for a child with overflow-x-auto or overflow-x-scroll
  const scrollableChild = slotElement.querySelector<HTMLElement>(
    '[style*="overflow-x"], .overflow-x-auto, .overflow-x-scroll'
  );

  if (scrollableChild) {
    return scrollableChild;
  }

  // Fallback: find any child that has scrollable content
  const children = Array.from(slotElement.children) as HTMLElement[];
  for (const child of children) {
    if (child.scrollWidth > child.clientWidth) {
      return child;
    }
  }

  return slotElement;
};

/**
 * Initialize the slider - can be called manually to re-initialize
 */
const initializeSlider = () => {
  // Find the actual scrollable element
  const scrollableElement = getScrollableElement();
  if (scrollableElement) {
    // Store the actual scrollable element
    actualScrollableElement.value = scrollableElement;
  }

  // Initial arrow visibility check
  updateArrowVisibility();

  // Setup scroll listener (remove old one first if exists)
  if (actualScrollableElement.value) {
    actualScrollableElement.value.removeEventListener("scroll", updateArrowVisibility);
    actualScrollableElement.value.addEventListener("scroll", updateArrowVisibility);
  }

  // Auto-scroll to element if specified (this should happen BEFORE animation)
  const didAutoScroll = autoScrollToElement();

  // Initialize bounce animation only if we didn't auto-scroll
  if (!didAutoScroll) {
    // Wait a bit to ensure content is fully rendered
    setTimeout(() => {
      initializeBounceAnimation();
    }, 150);
  }
};

/**
 * Update mobile state based on window width (sm breakpoint is 768px)
 */
const updateMobileState = () => {
  isMobile.value = window.innerWidth < 768;
};

/**
 * Handle window resize - updates mobile state and arrow visibility
 */
const handleResize = () => {
  if (props.hideArrowsOnMobile) {
    updateMobileState();
  }
  updateArrowVisibility();
};

// Lifecycle hooks
onMounted(() => {
  // Detect RTL
  isRTL.value = document.documentElement.dir === "rtl";

  // Detect initial mobile state
  if (props.hideArrowsOnMobile) {
    updateMobileState();
  }

  // Wait for next tick to ensure slot content is rendered
  nextTick(() => {
    initializeSlider();

    // Re-check arrow visibility on window resize
    window.addEventListener("resize", handleResize);
  });
});

// Expose methods for parent components
defineExpose({
  initializeSlider,
  updateArrowVisibility,
});

onUnmounted(() => {
  if (actualScrollableElement.value) {
    actualScrollableElement.value.removeEventListener("scroll", updateArrowVisibility);
  }
  cancelBounce?.();
  window.removeEventListener("resize", handleResize);
});
</script>

<template>
  <div ref="sliderContainer" class="slider-wrapper">
    <!-- Left Arrow - Client Only (not rendered in SSR) -->
    <ClientOnly>
      <button
        v-if="shouldShowArrows && showLeftArrow"
        data-ignore-heatmap
        class="slide-button-left"
        :class="[outerArrows ? 'left-6 top-1/2 md:-left-16 md:-translate-y-1/2' : 'left-6 top-1/2']"
        @click="handleLeftClick"
      >
        <left-arrow width="30" height="30"></left-arrow>
      </button>
    </ClientOnly>

    <!-- Slider Content - Rendered in SSR for SEO -->
    <div
      ref="sliderContent"
      class="slider-content"
      :class="{
        'blur-sm pointer-events-none': isAutoScrolling,
        'blur-none': !isAutoScrolling,
      }"
    >
      <slot />
    </div>

    <!-- Right Arrow - Client Only (not rendered in SSR) -->
    <ClientOnly>
      <button
        v-if="shouldShowArrows && showRightArrow"
        data-ignore-heatmap
        class="slide-button-right"
        :class="[outerArrows ? 'right-6 top-1/2 md:-right-16 md:-translate-y-1/2' : 'right-6 top-1/2']"
        @click="handleRightClick"
      >
        <right-arrow width="30" height="30"></right-arrow>
      </button>
    </ClientOnly>
  </div>
</template>

<style scoped>
.slider-wrapper {
  @apply relative w-full h-full;
}

.slider-content {
  @apply w-full h-full;
  transition: filter 0.3s ease-in-out;
}
</style>
